import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";

const ConfirmModal = ({
  title,
  message,
  onCancel,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColor = "bg-red-600",
  confirmHoverColor = "hover:bg-red-700",
}) => {
  const modalRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const isDestructive = confirmColor.includes("red");

  useEffect(() => {
    confirmBtnRef.current?.focus();

    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onCancel();
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") onCancel();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onCancel]);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.97, y: 4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 4 }}
          transition={{ duration: 0, ease: "easeOut" }}
          className="bg-white w-full max-w-md rounded-lg shadow-xl border border-gray-100"
          role="dialog"
          aria-modal="true"
        >
          <div className="px-5 pt-5 pb-4 flex items-start gap-3">
            <div
              className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                isDestructive ? "bg-red-50" : "bg-amber-50"
              }`}
            >
              <AlertTriangle
                size={18}
                className={isDestructive ? "text-red-600" : "text-amber-600"}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                {message}
              </p>
            </div>
          </div>

          <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 text-sm">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded"
            >
              {cancelText}
            </button>
            <button
              ref={confirmBtnRef}
              type="button"
              onClick={onConfirm}
              className={`px-3 py-1.5 font-medium text-white rounded ${confirmColor} ${confirmHoverColor}`}
            >
              {confirmText}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ConfirmModal;
